import { computed } from 'vue';
import store from '@/store';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import type { ListElements } from '@/typings/customilyElements/layer';
import { areElementsEqualByUuidOrId } from '@/utils/elementsUtils';

export interface UseLayerTreeItemProps {
    item: ListElements;
}

export function useLayerTreeItem(props: UseLayerTreeItemProps) {
    const { item } = props;

    const currentDraggedItem = computed(() => store.state.storeLayers?.currentDraggedItem);
    const currentTargetItem = computed(() => store.state.storeLayers?.currentTargetItem);

    const isCurrentDraggedItem = computed(() => {
        const currentUuid = item.element?.uuid;
        const draggedUuid = currentDraggedItem.value?.element?.uuid;

        return currentUuid && draggedUuid && currentUuid === draggedUuid;
    });

    const epsElements = computed(() => store.state.storeLayers?.rawElements?.pageEpsElements);

    const countParentLevels = (element: ListElements): number => {
        let levels = 0;
        let current = element;

        while (current?.parent) {
            levels++;
            current = current.parent;
        }

        // Adjust level if there's only one epsElement and current element is not of type eps
        if (epsElements.value?.length === 1 && element.type !== ListElementType.PageEps) {
            levels = Math.max(0, levels - 1); // Subtract 1 but ensure it doesn't go below 0
        }

        return levels;
    };

    const calculatedLevel = computed(() => {
        if (!isCurrentDraggedItem.value || !currentTargetItem.value) {
            return countParentLevels(props.item);
        }
        return countParentLevels(currentTargetItem.value);
    });

    // Computed properties
    const isSelected = computed(() => {
        const selectedItems = store.state.storeLayers?.selectedItems;
        return selectedItems?.some((selectedItem) => areElementsEqualByUuidOrId(selectedItem, item));
    });

    const isActiveEps = computed(() => {
        if (item.type === ListElementType.CanvasElement) return false;
        return store.getters['storeLayers/getIsActiveEps']?.(item.element.id);
    });

    const isActive = computed(() => {
        return store.getters['storeLayers/getIsActive']?.(item);
    });

    const flags = computed(() => {
        return store.state.storeLayers?.flags;
    });

    const itemStyle = computed(() => {
        return {
            paddingLeft: `${(calculatedLevel.value ?? 0) * 16}px` // 16px for each nesting level
        };
    });

    const isPrintArea = computed(() => item.element?.isPrintArea);
    const isProductGuidelines = computed(() => item.element?.isProductGuidelines);
    const isOverlay = computed(() => item.element?.isOverlay);
    const isVisible = computed(() => item.element?.visible);
    const isLocked = computed(() => item.element?.locked);

    // Event handlers
    const onElementClick = (event: Event) => {
        store.dispatch('storeLayers/setSelectedElementByIds', {
            ids: [item.element.uuid || item.element.id],
            event: event
        });
    };

    const onElementTitleChange = (newTitle: string | number) => {
        store.dispatch('storeLayers/updateElementName', {
            item,
            name: newTitle as string
        });
    };

    const onVisibilityClick = () => {
        if (flags.value?.readonly) return;
        store.dispatch('storeLayers/toggleVisibility', item.element);
    };

    const onLockClick = () => {
        if (flags.value?.readonly) return;
        store.dispatch('storeLayers/toggleLook', item.element);
    };

    const onPrintAreaClick = () => {
        if (!flags.value?.allowChangingPrintArea) return;
        store.state.storeLayers?.settings.events?.['togglePrintArea']?.({
            element: item.element
        });
    };

    const onOverlayClick = () => {
        if (!flags.value?.allowChangingOverlay) return;
        store.state.storeLayers?.settings.events?.['toggleOverlay']?.({
            element: item.element
        });
    };

    const onColorOptionClick = () => {
        if (!flags.value?.allowChangingColorOption) return;
        store.state.storeLayers?.settings.events?.['toggleColorOption']?.(item.element);
    };

    const onElementPositionChange = (newId: string | number) => {
        const newVal = Number(newId);
        if (isNaN(newVal)) return;

        item.element.position = newVal;
        store.state.storeLayers?.settings.events?.['positionChanged']?.(item.element);
    };

    return {
        // Computed properties
        isSelected,
        isActiveEps,
        isActive,
        flags,
        itemStyle,
        isPrintArea,
        isProductGuidelines,
        isOverlay,
        isVisible,
        isLocked,

        // Event handlers
        onElementClick,
        onElementTitleChange,
        onVisibilityClick,
        onLockClick,
        onPrintAreaClick,
        onOverlayClick,
        onColorOptionClick,
        onElementPositionChange
    };
}
